import type { Logger } from 'pino'
import Plausible from 'plausible-tracker'
import { logger } from './logger'

interface TrackerOptions {
  dev: boolean
  facebookPixelId?: string
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  fbq?: Function
  googleAdsId?: string
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  gtag?: Function
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  lintrk?: Function
  logger?: Logger
  plausible?: PlausibleReturn
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  snitcherOnConsent?: Function
}

class Tracker implements TrackerOptions {
  dev: boolean
  facebookPixelId?: string
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  fbq?: Function
  googleAdsId?: string
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  gtag?: Function
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  lintrk?: Function
  logger?: Logger
  plausible?: PlausibleReturn
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  snitcherOnConsent?: Function

  constructor(options: TrackerOptions = { dev: false }) {
    this.dev = options.dev
    this.facebookPixelId = options.facebookPixelId
    this.fbq = options.fbq
    this.googleAdsId = options.googleAdsId
    this.gtag = options.gtag
    this.lintrk = options.lintrk
    this.logger = options.logger
    this.plausible = options.plausible
    this.snitcherOnConsent = options.snitcherOnConsent
  }

  event(event: string, props = {}) {
    const { dev, fbq, gtag, logger, plausible } = this

    if (!event) return
    if (dev && logger) logger.info({ event, props }, 'Track event')
    if (plausible) plausible.trackEvent(event, { props })
    if (fbq) fbq('track', event, props)
    if (gtag) gtag('event', event, props)
  }

  facebookEvent(event: string, props = {}) {
    const { dev, fbq, logger } = this

    if (!event) return
    if (dev && logger) logger.info({ event, props }, 'Track Facebook event')
    if (fbq)
      fbq('track', event, props, {
        eventID: `${Date.now()}.${Math.floor(Math.random() * 9999) + 1}`
      })
  }

  facebookInit(props = {}) {
    const { dev, facebookPixelId, fbq, logger } = this

    if (dev && logger) logger.info({ props }, 'Track Facebook init')
    if (fbq && facebookPixelId) fbq('init', facebookPixelId, props)
  }

  googleAdsConversion(label: string, props = {}) {
    const { dev, googleAdsId, gtag, logger } = this

    if (!label) return
    if (dev && logger)
      logger.info({ label, props }, 'Track Google Ads conversion')
    if (googleAdsId && gtag)
      gtag('event', 'conversion', {
        ...props,
        send_to: `${googleAdsId}/${label}`
      })
  }

  googleEvent(event: string, props = {}) {
    const { dev, gtag, logger } = this

    if (!event) return
    if (dev && logger) logger.info({ event, props }, 'Track Google event')
    if (gtag) gtag('event', event, props)
  }

  linkedinTrack(conversionID: number) {
    const { dev, lintrk, logger } = this

    if (!conversionID) return
    if (dev && logger)
      logger.info({ conversionID }, 'Track LinkedIn conversion')
    if (lintrk) lintrk('track', { conversion_id: conversionID })
  }

  plausibleEvent(
    event: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props: { [key: string]: any; message?: string } = {},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    err?: any
  ) {
    const { dev, logger, plausible } = this

    if (!event) return
    if (
      err &&
      typeof err === 'object' &&
      'message' in err &&
      typeof err.message === 'string'
    ) {
      props.message = err.message
    }
    if (dev && logger) logger.info({ event, props }, 'Track Plausible event')
    if (plausible) plausible.trackEvent(event, { props })
  }

  async getDistinctId(email: string | null | undefined) {
    if (!email) return null

    const normalizedEmail = email.toLowerCase().trim()
    const encoder = new TextEncoder()
    const data = encoder.encode(normalizedEmail)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
    const truncatedHash = hashHex.slice(0, 20)
    const firstTwoBytes = data.slice(0, 2)
    const hexPrefix = Array.from(firstTwoBytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
      .padEnd(4, '0')

    return `di_v1_${hexPrefix}${truncatedHash}`
  }

  async getEmailHash(email: string | null | undefined) {
    if (!email) return null

    const normalizedEmail = email.toLowerCase().trim()
    const encoder = new TextEncoder()
    const data = encoder.encode(normalizedEmail)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')

    return hashHex
  }

  async getGAClientId() {
    const { gtag } = this

    if (gtag)
      return new Promise((resolve, reject) => {
        gtag('get', import.meta.env.VITE_GA_TRACKING_ID, 'client_id', resolve)
        setTimeout(() => reject(new Error('GA not responding')), 500)
      })
  }

  pageView({ canonicalPaths, headProps }: Vike.PageContext) {
    const { dev, gtag, logger, plausible } = this

    if (
      dev &&
      logger &&
      canonicalPaths &&
      canonicalPaths.relative &&
      headProps &&
      headProps.title
    )
      logger.info(
        { title: headProps.title, url: canonicalPaths.relative },
        'Track pagevew'
      )

    if (plausible && canonicalPaths && canonicalPaths.absolute)
      plausible.trackPageview({
        url: canonicalPaths.absolute
      })

    if (
      gtag &&
      canonicalPaths &&
      canonicalPaths.absolute &&
      headProps &&
      headProps.title
    ) {
      const originalURL = new URL(window.location.href)
      const canonicalURL = new URL(canonicalPaths.absolute)
      canonicalURL.search = originalURL.search

      gtag('event', 'page_view', {
        page_location: canonicalURL.toString(),
        page_title: headProps.title
      })
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
let fbq: Function | undefined
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
let gtag: Function | undefined
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
let lintrk: Function | undefined
let plausible: PlausibleReturn | undefined
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
let snitcherOnConsent: Function | undefined

// Configure trackers
if (!import.meta.env.SSR) {
  if ('fbq' in window && typeof window.fbq === 'function') fbq = window.fbq
  if ('gtag' in window && typeof window.gtag === 'function') gtag = window.gtag
  if ('lintrk' in window && typeof window.lintrk === 'function')
    lintrk = window.lintrk
  if (
    'snitcherOnConsent' in window &&
    typeof window.snitcherOnConsent === 'function'
  )
    snitcherOnConsent = window.snitcherOnConsent

  const domain = import.meta.env.VITE_DOMAIN
  const consent = window.localStorage.getItem('consent-selections')

  if (domain) {
    plausible = Plausible({
      domain,
      trackLocalhost: false
    })
  }

  if (typeof consent === 'string') {
    // Consent selections found
    const selections = consent.split(',')
    const advertising = selections.includes('advertising')
    const analytics = selections.includes('analytics')

    if (fbq) {
      fbq('consent', advertising ? 'grant' : 'revoke')
    }

    if (gtag) {
      gtag('consent', 'update', {
        ad_storage: advertising ? 'granted' : 'denied',
        ad_user_data: advertising ? 'granted' : 'denied',
        ad_personalization: advertising ? 'granted' : 'denied',
        analytics_storage: analytics ? 'granted' : 'denied',
        functionality_storage: 'granted',
        personalization_storage: 'granted',
        security_storage: 'granted'
      })
      gtag('set', 'ads_data_redaction', !advertising)
      gtag('set', 'allow_ad_personalization_signals', advertising)
      gtag('set', 'url_passthrough', false)
    }

    if (analytics && snitcherOnConsent) {
      snitcherOnConsent()
    }
  } else {
    // No consent selections found
    // GA4 and Facebook defaults already applied in onRenderHtml
  }
}

export const tracker = !import.meta.env.SSR
  ? new Tracker({
      dev: import.meta.env.DEV,
      facebookPixelId: import.meta.env.VITE_FACEBOOK_PIXEL_ID,
      fbq,
      googleAdsId: import.meta.env.VITE_GOOGLE_ADS_ID,
      gtag,
      lintrk,
      logger,
      plausible,
      snitcherOnConsent
    })
  : new Tracker()
